export const STAFF_TASK_STATUSES = ['open', 'in_progress', 'done'] as const;
export type StaffTaskStatus = (typeof STAFF_TASK_STATUSES)[number];

export const STAFF_TASK_PRIORITIES = ['low', 'normal', 'high'] as const;
export type StaffTaskPriority = (typeof STAFF_TASK_PRIORITIES)[number];

export const STAFF_TASK_ENTITY_TYPES = [
  'patient',
  'invoice',
  'referral',
  'letter',
  'appointment',
  'employee',
] as const;
export type StaffTaskEntityType = (typeof STAFF_TASK_ENTITY_TYPES)[number];

export const STAFF_TASK_SCOPES = ['mine', 'created_by_me', 'all', 'done'] as const;
export type StaffTaskScope = (typeof STAFF_TASK_SCOPES)[number];

export const STAFF_TASK_DEADLINE_FILTERS = ['overdue', 'today', 'this_week'] as const;
export type StaffTaskDeadlineFilter = (typeof STAFF_TASK_DEADLINE_FILTERS)[number];

export interface StaffTaskAssignee {
  user_id: string;
  name: string;
}

export interface StaffTaskLink {
  entity_type: StaffTaskEntityType;
  entity_id: string;
}

export interface StaffTask {
  id: string;
  clinic_id: string;
  title: string;
  description: string | null;
  status: StaffTaskStatus;
  priority: StaffTaskPriority;
  deadline_date: string | null;
  deadline_time: string | null;
  created_by: string | null;
  created_by_name: string | null;
  created_at: string;
  updated_at: string;
  completed_at: string | null;
  completed_by: string | null;
  completed_by_name: string | null;
  assignees: StaffTaskAssignee[];
  links: StaffTaskLink[];
}

export interface StaffTaskComment {
  id: string;
  task_id: string;
  author_id: string;
  author_name: string;
  body: string;
  created_at: string;
}

export function isStaffTaskStatus(value: string): value is StaffTaskStatus {
  return (STAFF_TASK_STATUSES as readonly string[]).includes(value);
}

export function isStaffTaskPriority(value: string): value is StaffTaskPriority {
  return (STAFF_TASK_PRIORITIES as readonly string[]).includes(value);
}

export function isStaffTaskEntityType(value: string): value is StaffTaskEntityType {
  return (STAFF_TASK_ENTITY_TYPES as readonly string[]).includes(value);
}

export function staffTaskIsOpen(status: StaffTaskStatus): boolean {
  return status !== 'done';
}

/** End of the local calendar day for a date-only deadline. */
export function staffTaskDeadlineInstant(
  deadlineDate: string | null,
  deadlineTime: string | null,
): Date | null {
  if (!deadlineDate) return null;
  if (deadlineTime && /^\d{2}:\d{2}/.test(deadlineTime)) {
    const iso = `${deadlineDate}T${deadlineTime.slice(0, 8)}`;
    const parsed = new Date(iso);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  }
  const end = new Date(`${deadlineDate}T23:59:59`);
  return Number.isNaN(end.getTime()) ? null : end;
}

export function staffTaskIsOverdue(
  deadlineDate: string | null,
  deadlineTime: string | null,
  status: StaffTaskStatus,
  now: Date = new Date(),
): boolean {
  if (!staffTaskIsOpen(status)) return false;
  const instant = staffTaskDeadlineInstant(deadlineDate, deadlineTime);
  return instant != null && instant.getTime() < now.getTime();
}

function startOfLocalDay(date: Date): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addLocalDays(date: Date, days: number): Date {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate() + days);
}

export function staffTaskDeadlineBucket(
  deadlineDate: string | null,
  deadlineTime: string | null,
  now: Date = new Date(),
): StaffTaskDeadlineFilter | null {
  const instant = staffTaskDeadlineInstant(deadlineDate, deadlineTime);
  if (!instant) return null;
  const today = startOfLocalDay(now);
  const tomorrow = addLocalDays(today, 1);
  const weekEnd = addLocalDays(today, 7 - ((today.getDay() + 6) % 7));
  if (instant.getTime() < today.getTime()) return 'overdue';
  if (instant.getTime() < tomorrow.getTime()) return 'today';
  if (instant.getTime() < weekEnd.getTime()) return 'this_week';
  return null;
}

export function staffTaskMatchesDeadlineFilter(
  deadlineDate: string | null,
  deadlineTime: string | null,
  status: StaffTaskStatus,
  filter: StaffTaskDeadlineFilter | null,
  now: Date = new Date(),
): boolean {
  if (!filter) return true;
  switch (filter) {
    case 'overdue':
      return staffTaskIsOverdue(deadlineDate, deadlineTime, status, now);
    case 'today':
      return staffTaskDeadlineBucket(deadlineDate, deadlineTime, now) === 'today';
    case 'this_week':
      return (
        staffTaskDeadlineBucket(deadlineDate, deadlineTime, now) === 'today' ||
        staffTaskDeadlineBucket(deadlineDate, deadlineTime, now) === 'this_week'
      );
    default: {
      const _exhaustive: never = filter;
      return _exhaustive;
    }
  }
}

export function compareStaffTasks(a: StaffTask, b: StaffTask, now: Date = new Date()): number {
  const aOpen = staffTaskIsOpen(a.status) ? 0 : 1;
  const bOpen = staffTaskIsOpen(b.status) ? 0 : 1;
  if (aOpen !== bOpen) return aOpen - bOpen;

  const aOverdue = staffTaskIsOverdue(a.deadline_date, a.deadline_time, a.status, now) ? 0 : 1;
  const bOverdue = staffTaskIsOverdue(b.deadline_date, b.deadline_time, b.status, now) ? 0 : 1;
  if (aOverdue !== bOverdue) return aOverdue - bOverdue;

  const aDue = staffTaskDeadlineInstant(a.deadline_date, a.deadline_time)?.getTime() ?? Number.POSITIVE_INFINITY;
  const bDue = staffTaskDeadlineInstant(b.deadline_date, b.deadline_time)?.getTime() ?? Number.POSITIVE_INFINITY;
  if (aDue !== bDue) return aDue - bDue;

  return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
}

export function staffTaskEntityHref(link: StaffTaskLink): string | null {
  switch (link.entity_type) {
    case 'patient':
      return `/patients/${link.entity_id}`;
    case 'invoice':
      return `/economy/invoices/${link.entity_id}`;
    case 'referral':
      return `/henvisninger/${link.entity_id}`;
    case 'letter':
      return `/medcom/letters/${encodeURIComponent(link.entity_id)}`;
    case 'appointment':
      return '/appointments';
    case 'employee':
      return null;
    default: {
      const _exhaustive: never = link.entity_type;
      return _exhaustive;
    }
  }
}

export function countMyOpenTasks(tasks: readonly StaffTask[], userId: string | null): number {
  if (!userId) return 0;
  return tasks.filter(
    (task) => staffTaskIsOpen(task.status) && task.assignees.some((assignee) => assignee.user_id === userId),
  ).length;
}
